// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { forwardRef, useId } from 'react';
import './TextArea.css';

interface TextAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  fluid?: boolean;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      error,
      helperText,
      required,
      disabled,
      fluid = false,
      className = '',
      value,
      id: idProp,
      ...rest
    },
    ref,
  ) => {
    const autoId = useId();
    const id = idProp ?? autoId;
    const isFilled = value !== undefined && value !== '';

    const wrapperClasses = [
      'fin-textarea',
      error && 'fin-textarea--error',
      disabled && 'fin-textarea--disabled',
      isFilled && 'fin-textarea--filled',
      fluid && 'fin-textarea--fluid',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClasses}>
        {label && (
          <label className="fin-textarea__label" htmlFor={id}>
            {label}
            {required && <span className="fin-textarea__required">*</span>}
          </label>
        )}
        <textarea
          ref={ref}
          id={id}
          className="fin-textarea__field"
          disabled={disabled}
          required={required}
          value={value}
          aria-invalid={!!error}
          aria-describedby={
            error ? `${id}-error` : helperText ? `${id}-helper` : undefined
          }
          {...rest}
        />
        {error && (
          <p className="fin-textarea__error" id={`${id}-error`} role="alert">
            {error}
          </p>
        )}
        {!error && helperText && (
          <p className="fin-textarea__helper" id={`${id}-helper`}>
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

TextArea.displayName = 'TextArea';
